import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { Link, useSearchParams } from 'react-router-dom'
import {
  Box,
  Paper,
  Typography,
  TextField,
  IconButton,
  InputAdornment,
} from '@mui/material'
import { Visibility, VisibilityOff } from '@mui/icons-material'
import { useAuth } from '@context/AuthContext'
import { useBranding } from '@context/BrandingContext'
import Button from '@components/common/Button'

const Login = () => {
  const { login } = useAuth()
  const { orgName, orgLogoUrl } = useBranding()
  const [searchParams] = useSearchParams()
  const [showPassword, setShowPassword] = useState(false)
  const [loading, setLoading] = useState(false)

  // Get redirect URL from query params (e.g., /verify/ABC123)
  const redirectUrl = searchParams.get('redirect')

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: {
      username: '',
      password: '',
    },
  })

  const onSubmit = async (data) => {
    setLoading(true)
    // Pass redirect URL to login function (it will navigate automatically)
    await login(data.username, data.password, redirectUrl)
    setLoading(false)
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#F5F7FA',
        p: 2,
      }}
    >
      <Paper
        elevation={0}
        sx={{
          maxWidth: 420,
          width: '100%',
          p: 4,
          borderRadius: 3,
          border: '1px solid #e2e8f0',
        }}
      >
        {/* Logo - DuoNep is the permanent product brand */}
        <Box textAlign="center" mb={4}>
          <Box
            component="img"
            src="/images/duonep-logo.png"
            alt="DuoNep"
            sx={{
              maxWidth: 260,
              height: 'auto',
              mb: 2,
            }}
          />
          <Typography color="text.secondary" sx={{ fontSize: '0.875rem' }}>
            Computerized Maintenance Management System
          </Typography>
        </Box>

        {/* Login Form */}
        <form onSubmit={handleSubmit(onSubmit)}>
          <TextField
            {...register('username', { required: 'Username is required' })}
            fullWidth
            label="Username"
            error={!!errors.username}
            helperText={errors.username?.message}
            margin="normal"
            autoComplete="username"
            autoFocus
          />

          <TextField
            {...register('password', { required: 'Password is required' })}
            fullWidth
            label="Password"
            type={showPassword ? 'text' : 'password'}
            error={!!errors.password}
            helperText={errors.password?.message}
            margin="normal"
            autoComplete="current-password"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <Button
            type="submit"
            fullWidth
            loading={loading}
            sx={{ mt: 3, mb: 2, py: 1.5 }}
          >
            Sign In
          </Button>

          <Box textAlign="center">
            <Link
              to="/forgot-password"
              style={{
                color: '#1B5E7A',
                textDecoration: 'none',
                fontSize: '0.875rem',
              }}
            >
              Forgot your password?
            </Link>
          </Box>
        </form>

        {/* Partner co-brand spot */}
        <Box
          textAlign="center"
          mt={4}
          pt={3}
          sx={{ borderTop: '1px solid #e2e8f0' }}
        >
          {orgLogoUrl && (
            <Box
              component="img"
              src={orgLogoUrl}
              alt={orgName}
              sx={{ maxHeight: 40, maxWidth: 160, height: 'auto', mb: 0.5 }}
            />
          )}
          <Typography variant="body2" color="text.primary" fontWeight={600} display="block">
            {orgName}
          </Typography>
        </Box>
      </Paper>
    </Box>
  )
}

export default Login
